import { useLocation, useNavigate } from "react-router-dom";
import LockSVG from "../../content/img/lock.svg";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "oidc-react";
import { Button } from "@etframework/ui.components";
import { useRequestBoardAccess } from "../../data/mutations/boardMutations";
import useModalEvent from "../../hooks/useModalEvent";
import { RequestBoardAccessModal } from "../../layout/portals/RequestAccessModal";
import {
  useBoards,
  useUserBoardPermissions,
} from "../../data/queries/boardQueries";
import { useUserHasPendingBoardPermissionRequests } from "../../data/queries/boardPermissionRequestQueries";
import { useEffect, useMemo } from "react";
import { IconLoading } from "../../components/Loading/IconLoading";
import { useQueryClient } from "@tanstack/react-query";

export const InvalidBoardAccess = () => {
  const { userData } = useAuth();
  const { t } = useTranslation(["errorPages"]);
  const { ModalEvents, toggleModal } = useModalEvent();
  const queryClient = useQueryClient();
  const {
    state: { boardRequestingAccessFor },
  } = useLocation();

  const {
    data: boards,
    isPending: isPendingBoards,
    isSuccess: isSuccessBoards,
  } = useBoards();
  const {
    data: userBoardPermissions,
    isPending: isPendingUserBoardPermissions,
    isSuccess: isSuccessUserBoardPermissions,
  } = useUserBoardPermissions();
  const {
    data: userHasPendingRequests,
    isPending: isPendingUserHasPendingRequests,
    isSuccess: isSuccessUserHasPendingRequests,
  } = useUserHasPendingBoardPermissionRequests();

  const firstBoardWithPermissions = useMemo(() => {
    if (userBoardPermissions && boards) {
      return boards.find((board) =>
        userBoardPermissions.some(
          (permission) => permission.BoardId === board.Id
        )
      );
    }

    return null;
  }, [userBoardPermissions, boards]);

  const hasPermissionToAccessDesiredBoard = userBoardPermissions?.some(
    (permission) => {
      return permission.BoardId === boardRequestingAccessFor?.Id;
    }
  );

  // NOTE(ivelez): if `boardRequestingAccessFor` is truthy, it means the user selected a board and they don't have access to it.
  const hasSelectedBoard = Boolean(boardRequestingAccessFor);
  const hasAnyBoardPermissions = Boolean(firstBoardWithPermissions);
  const hasAccessToDesiredBoard = Boolean(hasPermissionToAccessDesiredBoard);

  const redirectToDueDates =
    hasAnyBoardPermissions &&
    (hasSelectedBoard ? hasAccessToDesiredBoard : true);

  const { mutate: requestBoardAccess, isPending } = useRequestBoardAccess();

  const navigate = useNavigate();

  const isAdmin = userData?.profile?.role === "Administrator";
  const hasPermissions = userBoardPermissions?.length > 0;

  const isAdminAndHasPermissions = isAdmin && hasPermissions;
  const isAdminAndNoPermissions = isAdmin && !hasPermissions; // First time in DueDates

  const handleAdminAddMyself = () => {
    requestBoardAccess(
      {
        boardIds: [boardRequestingAccessFor?.Id],
      },
      {
        onSuccess: () => {
          navigate(
            `/duedates/views/assignedtome/?boardId=${boardRequestingAccessFor?.Id}`
          );
        },
        onError: (error) => {
          console.error(error);
        },
      }
    );
  };

  const handleAdminRequestBoardAccess = () => {
    toggleModal(ModalEvents.RequestBoardAccessModal);
  };

  const handleUserRequestBoardAccess = () => {
    toggleModal(ModalEvents.RequestBoardAccessModal);
  };

  const handleRequestAccess = () => {
    if (isAdminAndHasPermissions) {
      handleAdminAddMyself();
    } else if (isAdminAndNoPermissions) {
      handleAdminRequestBoardAccess();
    } else if (!isAdmin) {
      handleUserRequestBoardAccess();
    } else {
      throw new Error("Could not handle request board access.");
    }
  };

  useEffect(() => {
    if (
      redirectToDueDates &&
      isSuccessBoards &&
      isSuccessUserBoardPermissions &&
      isSuccessUserHasPendingRequests
    ) {
      navigate(
        `/duedates/views/assignedtome/?boardId=${
          hasSelectedBoard
            ? boardRequestingAccessFor?.Id
            : firstBoardWithPermissions.Id
        }`
      );
    }
  }, [
    boards,
    userBoardPermissions,
    userHasPendingRequests,
    isSuccessBoards,
    isSuccessUserBoardPermissions,
    isSuccessUserHasPendingRequests,
    navigate,
    firstBoardWithPermissions,
    redirectToDueDates,
  ]);

  if (
    isPendingBoards ||
    isPendingUserBoardPermissions ||
    isPendingUserHasPendingRequests
  ) {
    return <IconLoading />;
  }

  if (redirectToDueDates) return null;

  return (
    <>
      <div className="error-page-container">
        <img
          src={LockSVG}
          alt="Error illustration"
          className="error-image unauthorized"
          style={{ width: "130px" }}
        />
        <h1 className="error-title">
          {isAdminAndHasPermissions &&
            t("InvalidBoardAccess.titleTo", {
              boardName: boardRequestingAccessFor?.Name,
            })}
          {isAdminAndNoPermissions && t("InvalidBoardAccess.title")}
          {!isAdmin && t("InvalidBoardAccess.title")}
        </h1>
        <p className="error-message">
          <Trans i18nKey="errorPages:InvalidBoardAccess.message" />
        </p>
        <div className="w-52">
          <Button onClick={handleRequestAccess} loading={isPending}>
            {isAdminAndHasPermissions && t("InvalidBoardAccess.addMyself")}
            {isAdminAndNoPermissions &&
              t("InvalidBoardAccess.addMyselfToDueDates")}
            {!isAdmin && t("InvalidBoardAccess.requestAccess")}
          </Button>
        </div>
        <div className="mt-4">
          <Button
            onClick={() => {
              queryClient.invalidateQueries(["get-user-board-permissions"]);
              navigate("/");
            }}
            className="secondary"
          >
            <i className="fa-regular fa-house-chimney me-2 et-dark-gray"></i>
            <span className="et-dark-blue">{t("HomeBtn")}</span>
          </Button>
        </div>
      </div>
      <RequestBoardAccessModal isAdmin={isAdmin} />
    </>
  );
};
